import { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import { 
    enableUser, 
    getUserInfo, 
    getUserInfoWithId, 
    suspendUser, 
    getAllFeatureFlagNames, 
    getAllScheduleNames,
    getOrganizationInfo,
    getUserOrganisationFeatureFlags,
} from '../../../Services/Search.services';
import { toast } from 'react-toastify';
import SkeletonComponent from '../../SkeletonComponent';
import { useSelector } from 'react-redux';
import { Header } from '../../Shared/Header/Header';
import styles from './UserDetailsTable.module.css';
import { Apps } from '../../Shared/Apps';
import { camelCaseToText, datetimeToFormattedString } from '../../../Utils/Utils';
import { UrlActionType, UrlButton } from '../../Shared/UrlButton/UrlButton';
import { CircularProgress } from '@mui/material';
import { AvlSwitch } from '../../Shared/Inputs/Switch/AvlSwitch';
import { green } from '@mui/material/colors';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import { AvlCheckbox } from '../../Shared/Inputs/Checkbox/AvlCheckbox';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export default function UserDetailsTable({ userId, email }) {
    const [isUserLoading, setIsUserLoading] = useState(true);
    const [isUserStatusLoading, setIsUserStatusLoading] = useState(false);
    const [isOrgLoading, setIsOrgLoading] = useState(true);
    const [details, setDetails] = useState({});
    const [isUserDisabled, setIsUserDisabled] = useState(null);
    const [allSchedules, setAllSchedules] = useState({ schedules: [], scottishSchedules: [] });
    const [allFeatureFlagNames, setAllFeatureFlagNames] = useState([]);
    const [allFeatureFlagsByParameter, setAllFeatureFlagsByParameter] = useState({ byName: [], byOrganisation: [] });
    const [schedulesPerOrganisation, setSchedulesPerOrganisation] = useState({ schedules: [], scottishSchedules: [], featureFLags: {} });
    const [isEditMode, setIsEditMode] = useState(false);
    const [schedulesExpanded, setSchedulesExpanded] = useState(false);
    const isAdmin = useSelector((state) => state.auth.isAdmin);
    const isLoading = isUserLoading || isOrgLoading;

    const displayName = (
        <span className={details.displayName ?? styles.emptyPlaceholder}>
            {details.displayName ?? 'No display name'}
        </span>
    );
    const displayEmail = (
        <span className={details.email ?? styles.emptyPlaceholder}>
            {details.email ?? 'No email'}
        </span>
    );

    const handleStatus = (isEnabled) => {
        let actionPromise = null;
        setIsUserStatusLoading(true);

        if (isEnabled) {
            actionPromise = enableUser(userId)
                .then(() => {
                    toast.success('User enabled successfully.');
                });
        } else {
            actionPromise = suspendUser(userId)
                .then(() => {
                    toast.warn('User suspended.');
                });
        }

        actionPromise.finally(() => {
            setIsUserDisabled(!isEnabled);
            setIsUserStatusLoading(false);
        });
    };

    useEffect(() => {
        fetchAllFeatureFlagNames();
        fetchSchedulesAll();
    }, []);

    useEffect(() => {
        fetchAllFeatureFlagsByParameter();
        fetchOrganisationData();
    }, [details.organisation]);

    const fetchAllFeatureFlagsByParameter = async () => {
        if (!details.organisation || !details.email) return;

        setIsOrgLoading(true);

        try {
            const { data } = await getUserOrganisationFeatureFlags(details.organisation, userId);
            const items = data?.items ?? [];

            const byName = items.filter((item) => item.user_name === details.email);
            const byOrganisation = items.filter((item) => item.user_name === '*');

            setAllFeatureFlagsByParameter({ byName, byOrganisation });
        } catch (error) {
            toast.error('Failed to load feature flags by organisation name. Please try again later.');
        } finally {
            setIsOrgLoading(false);
        }
    };

    const fetchOrganisationData = async () => {
        if (!details.organisation) return;

        setIsOrgLoading(true);

        try {
            const organisation = await getOrganizationInfo(details.organisation);
            setSchedulesPerOrganisation({ 
                schedules: organisation.schedules ?? [],
                scottishSchedules: organisation.scottishSchedules ?? [],
                featureFLags: { isRosEnabled: organisation.isRosEnabled }
            });
        } catch (error) {
            toast.error('Failed to load organisation data. Please try again later.');
        } finally {
            setIsOrgLoading(false);
        }
    };

    const fetchAllFeatureFlagNames = async () => {
        try {
            const flagNames = await getAllFeatureFlagNames();
            setAllFeatureFlagNames(flagNames.feature_flags);
        } catch (err) {
            toast.error('Failed to load flag names. Please try again later.');
        } 
    };
        
    const fetchSchedulesAll = async () => {
        try {
            const schedules = await getAllScheduleNames();
            setAllSchedules({ 
                scottishSchedules: schedules.scottish_schedules ?? [], 
                schedules: schedules.hmlr_schedules ?? [] 
            });
        } catch (err) {
            toast.error('Failed to load schedule names. Please try again later.');
        }
    };

    const handleUserScheduleToggle = (scheduleKey, isScottish = false) => {
        const field = isScottish ? 'scottishSchedules' : 'schedules';
        const currentSchedules = details[field] || [];
        const hasSchedule = currentSchedules.includes(scheduleKey);
    
        let nextSchedules;

        if (hasSchedule) {
            nextSchedules = currentSchedules.filter((s) => s !== scheduleKey);
        } else {
            nextSchedules = [...currentSchedules, scheduleKey];
        }
    
        setDetails((prev) => ({
            ...prev,
            [field]: nextSchedules,
        }));
    };

    const handleFeatureFlagToggle = (flagKey, currentValue) => {
        const nextValue = currentValue === true ? false : true; 
    
        setDetails((prev) => ({
            ...prev,
            featureFlags: {
                ...prev.featureFlags,
                [flagKey]: nextValue,
            },
        }));
    };

    const findFeatureFlagValue = (flags, name) => 
        flags.find((el) => el.name === name)?.is_enabled ?? null;

    const StatusTable = ({ title, rows, renderRow }) => {
        if (!rows.length) return <div>No data available</div>;
    
        return (
            <div className={styles.scheduleTable}>
                <div className={styles.schedulesHeader}>
                    <div>{title}</div>
                    <div>Plan</div>
                    <div>Org Feature Flags</div>
                    <div>User Feature Flags</div>
                    <div>Result</div>
                </div>
    
                <div>
                    {rows.map(renderRow)}
                </div>
            </div>
        );
    };

    const StatusCheckbox = ({
        value,
        isDisabled,
        onChange,
    }) => {
        const isAbsent = value === null || value === undefined;
        const isNegative = value === false;
    
        return (
            <AvlCheckbox
                checked={value === true}
                disabled={isDisabled}
                indeterminate={isAbsent || isNegative}
                onChange={onChange}
                icon={<CheckBoxOutlineBlankIcon />}
                checkedIcon={<CheckBoxIcon />}
                indeterminateIcon={
                    isAbsent
                        ? <CheckBoxOutlineBlankIcon />
                        : <DisabledByDefaultIcon />
                }
                sx={{
                    margin: 0,
                    padding: 0,
                    '&.Mui-checked': { color: '#8ac291' },
                    '&.MuiCheckbox-indeterminate': {
                        color: !isDisabled && isAbsent
                            ? 'rgba(0,0,0,0.54)'
                            : isNegative
                                ? '#c51031'
                                : '',
                        opacity: isNegative ? 0.4 : 1,
                    },
                }}
            />
        );
    };

    const SchedulesList = ({ all, user, isScottish }) => {
        const userSet = new Set(user || []);
        const schedules = isScottish
            ? schedulesPerOrganisation.scottishSchedules
            : schedulesPerOrganisation.schedules;

        return (
            <StatusTable
                title={isScottish ? 'Scottish schedule' : 'Schedule'}
                rows={[...all].sort()}
                renderRow={(key) => {
                    const name = camelCaseToText(key);
    
                    const orgValue = findFeatureFlagValue(allFeatureFlagsByParameter.byOrganisation, name);
                    const userValue = findFeatureFlagValue(allFeatureFlagsByParameter.byName, name);
    
                    return (
                        <div key={key} className={styles.schedulesRow}>
                            <div>{name}</div>
                            <div>
                                <StatusCheckbox
                                    value={schedules.includes(name)}
                                    isDisabled
                                />
                            </div>
                            <div>
                                <StatusCheckbox
                                    value={orgValue}
                                    isDisabled
                                />
                            </div>
                            <div>
                                <StatusCheckbox
                                    value={userValue}
                                    isDisabled={!isEditMode}
                                    onChange={() => handleUserScheduleToggle(key, isScottish)}
                                />
                            </div>
                            <div>
                                <div
                                    className={`${styles.featureFlagIndicator} ${
                                        userSet.has(key)
                                            ? styles.featureFlagEnabled
                                            : styles.featureFlagDisabled
                                    }`}
                                />
                            </div>
                        </div>
                    );
                }}
            />
        );
    };

    const toCamelCase = (str) => {
        if (!str) return '';

        return str
            .replace(/[-_]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ''))
            .replace(/^./, (c) => c.toLowerCase()); 
    };

    const FeatureFlagsList = () => {
        const flags = allFeatureFlagNames.map((raw) => ({
            raw,
            camel: toCamelCase(raw),
        })).sort();

        return (
            <StatusTable
                title='Feature Flags'
                rows={flags}
                renderRow={({ raw, camel }) => {
                    const orgValue = findFeatureFlagValue(allFeatureFlagsByParameter.byOrganisation, raw);
                    const userValue = findFeatureFlagValue(allFeatureFlagsByParameter.byName, raw);
    
                    const result = details.featureFlags?.[camel];
                    const featureFlagsFromOrgEndpoint = Object.keys(schedulesPerOrganisation.featureFLags);
    
                    return (
                        <div key={raw} className={styles.schedulesRow}>
                            <div>{raw}</div>
                            <div>
                                <StatusCheckbox
                                    value={featureFlagsFromOrgEndpoint.includes(toCamelCase(raw))}
                                    isDisabled
                                />
                            </div>
                            <div>
                                <StatusCheckbox
                                    value={orgValue}
                                    isDisabled
                                />
                            </div>
                            <div>
                                <StatusCheckbox
                                    value={userValue}
                                    isDisabled={!isEditMode}
                                    onChange={() => handleFeatureFlagToggle(camel, userValue)}
                                />
                            </div>
                            <div>
                                <div
                                    className={`${styles.featureFlagIndicator} ${
                                        result
                                            ? styles.featureFlagEnabled
                                            : styles.featureFlagDisabled
                                    }`}
                                />
                            </div>
                        </div>
                    );
                }}
            />
        );
    };

    useEffect(() => {
        getUserInfoWithId(userId)
            .then((res) => {
                if (res.status === 200) {
                    setDetails(res.data);
                    setIsUserDisabled(res.data.disabled);
                    setIsUserLoading(false);
                } else {
                    if ((res && res.stack && res.message)) {
                        if (res.request.status === 404) {
                            getUserInfo(email)
                                .then((res) => {
                                    if (res.status === 200) {
                                        setDetails(res.data);
                                        setIsUserDisabled(res.data.disabled);
                                        setIsUserLoading(false);
                                    } else {
                                        if ((res && res.stack && res.message)) {
                                            setDetails(null);
                                            setIsUserLoading(false);
                                        }
                                    }
                                });
                        }
                    } else {
                        //  probably access point related problem
                        setDetails(null);
                        setIsUserLoading(false);
                        toast.warn('Probably something went wrong at the access point. Please try again later.');
                    }
                }
            })
            .catch(() => {
                setDetails(null);
                setIsUserLoading(false);
            });
    }, [email, userId]);

    const statusController = () => {
        return (
            <div
                style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    height: '24px',
                }}
            >
                <div style={{ minWidth: '83px' }}>
                    {
                        isUserDisabled
                            ? (<span style={{ color: '#c51031' }}>Suspended</span>)
                            : (<span style={{ color: '#8ac291' }}>Enabled</span>)
                    }
                </div>
                <AvlSwitch
                    checked={!isUserDisabled}
                    disabled={!isAdmin || isUserStatusLoading}
                    onSwitch={handleStatus}
                />
                <CircularProgress
                    size={24}
                    sx={{
                        color: green[500],
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-12px',
                        marginLeft: '-30px',
                        display: isUserStatusLoading ? 'block' : 'none',
                    }}
                />
            </div>
        );
    };

    const item = (label, data) => {
        return (
            <div className={styles.item}>
                <span className={styles.itemLabel}>{label}</span>
                <div className={`${styles.itemBody} ${!data && styles.itemBodyEmpty}`}>{data ?? 'None'}</div>
            </div>
        );
    };

    const customizeDate = (date) => {
        return datetimeToFormattedString(date === 'Unknown' ? null : date);
    };

    const customizeUrl = (url, placeholder) => {
        return url
            ? (
                <div style={{ height: '30px' }}>
                    <UrlButton
                        url={url}
                        action={UrlActionType.openInNewTab}
                    />
                </div>
            )
            : placeholder;
    };

    const customizeUrlWithLabel = (label, url) => {
        return (
            <div className={styles.labelWithUrl}>
                {label}
                <div style={{ height: '30px' }}>
                    <UrlButton
                        url={url}
                        action={UrlActionType.openInNewTab}
                    />
                </div>
            </div>
        );
    };

    const AllowFeatureEditingToggle = () => {
        return (
            <AvlSwitch
                value='edit'
                label='Enable editing for schedules & feature flags'
                checked={isEditMode}
                onSwitch={() => setIsEditMode((v) => !v)}
                sx={{ height: '30px' }}
            />
        );
    };

    const getOrgPageUrl = (orgName) => {
        return `/search/organisations/${orgName}/`;
    };

    return (
        <Paper
            sx={{
                p: 2,
                margin: 'auto',
                flexGrow: 1,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: (theme) =>
                    theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
            }}
        >
            <div className={styles.header}>
                <Header
                    title={(
                        <div className={styles.headerTitle}>
                            {displayName}
                            {'|'}
                            {displayEmail}
                        </div>
                    )}
                    actionButtons={[
                        <div key='toggle-status' className={styles.toggleStatusContainer}>
                            {/* {AllowFeatureEditingToggle()} */}
                            {statusController()}
                        </div>
                    ]}
                />
            </div>
            {isLoading
                ? (<SkeletonComponent />)
                : details
                    ? (
                        <div className={styles.detailsContainer}>
                            <div className={styles.detailsLeft}>
                                {!!details.apps && item('Apps', (<Apps apps={details.apps.map((app) => app.key)} />))}
                                {item(
                                    'Organisation',
                                    customizeUrlWithLabel(details.organisation, getOrgPageUrl(details.organisation))
                                )}
                                {item('Hourly Rate', details.hourlyRate)}
                                {item('First Sign-in', customizeDate(details.firstSignIn))}
                                {item('Last Sign-in', customizeDate(details.lastSignIn))}
                                {item('Sign-up Link', customizeUrl(details.signUpLink))}
                                {item('Sign-in Method', details.signInMethod)}
                                <div className={styles.schedulesWrapper}>
                                    <Accordion expanded={schedulesExpanded} onChange={() => setSchedulesExpanded(!schedulesExpanded)}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <div>Schedules & Feature Flags</div>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                                <div className={styles.schedulesRowContainer}>
                                                    <SchedulesList
                                                        all={allSchedules.schedules}
                                                        user={details?.schedules}
                                                    />
                                                    <SchedulesList
                                                        all={allSchedules.scottishSchedules}
                                                        user={details?.scottishSchedules}
                                                        isScottish
                                                    />
                                                </div>
                                                {!!details.featureFlags && <FeatureFlagsList />}
                                            </div>
                                        </AccordionDetails>
                                    </Accordion>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <Stack sx={{ width: '100%' }} spacing={2}>
                            <Alert severity="warning">Records not found!</Alert>
                        </Stack>
                    )
                }
        </Paper>
    );
}
